/**
 * Token Budget Monitor Hook
 * Monitors Pipedrive API token usage and shows warnings when approaching limits
 * - Polls every 5 minutes for budget status
 * - Shows persistent toast at 80% threshold
 * - Provides action to view details in Settings modal
 */

import { useEffect, useRef } from 'react';
import { useToastStore } from '@shared/store/toastStore';
import { tokenUsageService } from '@shared/services/tokenUsageService';

const WARNING_THRESHOLD = 80; // Show warning at 80%
const POLL_INTERVAL = 5 * 60 * 1000; // 5 minutes

export function useTokenBudgetMonitor() {
  const { addToast } = useToastStore();
  const hasShownWarning = useRef(false);

  useEffect(() => {
    const checkBudget = async () => {
      try {
        // Use tokenUsageService which properly handles API routing through apiClient
        // This ensures correct base URL, auth headers, and proxy configuration
        const data = await tokenUsageService.getStatus();

        if (data.percentage >= WARNING_THRESHOLD && !hasShownWarning.current) {
          hasShownWarning.current = true;
          addToast({
            variant: 'warning',
            title: 'API Token Warning',
            message: `Pipedrive usage at ${Math.round(data.percentage)}% (${data.used.toLocaleString()} / ${data.budget.toLocaleString()} tokens)`,
            duration: 0, // Persistent (0 = no auto-dismiss)
            action: {
              label: 'View Details',
              onClick: () => {
                // Dispatch custom event to open Settings modal
                // This will be caught by App.tsx or SettingsModal
                window.dispatchEvent(new CustomEvent('open-settings', {
                  detail: { section: 'api-usage' }
                }));
              },
            },
          });
          console.log('[TokenBudgetMonitor] Warning shown:', data);
        } else if (data.percentage < WARNING_THRESHOLD) {
          // Reset warning if usage drops below threshold
          hasShownWarning.current = false;
        }
      } catch (error) {
        console.error('[TokenBudgetMonitor] Failed to check token budget:', error);
      }
    };

    // Check immediately on mount
    checkBudget();

    // Set up polling interval
    const interval = setInterval(checkBudget, POLL_INTERVAL);

    return () => clearInterval(interval);
  }, [addToast]);
}
